import { Link, useFetcher } from "react-router";
import { Camera } from "lucide-react";
import type { Feedback } from "@repo/shared-types";
import { kindLabel, statusLabel } from "@/lib/feedback";
import { timeAgo } from "@/lib/inbox";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";

/**
 * One feedback on the board (0091), and the pieces its page shares: the
 * pills that say what it is, who sent it and when, and "Me too". The message
 * is the link, stretched over the card (as an event's title is), so Me too
 * sits above it and is tapped on its own.
 */

/** What a status looks like: New is plain, the rest speak in the tokens' colours. */
const TONES: Record<string, string> = {
  planned: "bg-info-muted text-info",
  done: "bg-success-muted text-success",
  wont_do: "bg-muted text-muted-foreground",
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="secondary" className={cn(TONES[status])}>
      {statusLabel(status)}
    </Badge>
  );
}

/** Kind and status as pills (the kind unless the page's title says it), then who sent it and how long ago. */
export function FeedbackMeta({ feedback, now, kind = true }: { feedback: Feedback; now: number; kind?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {kind && <Badge variant="outline">{kindLabel(feedback.kind)}</Badge>}
      <StatusBadge status={feedback.status} />
      <span>
        @{feedback.author.username} · {timeAgo(feedback.createdAt, now)}
      </span>
    </div>
  );
}

/** The runner's one note, under what it answers. */
export function ReplyNote({ reply, clamp = false }: { reply: string; clamp?: boolean }) {
  return (
    <div className="space-y-0.5 rounded-2xl bg-muted px-3 py-2 text-sm">
      <p className="text-xs font-medium text-muted-foreground">Reply</p>
      <p className={cn("whitespace-pre-line wrap-anywhere", clamp && "line-clamp-3")}>{reply}</p>
    </div>
  );
}

/** A screenshot's mark on a card: it is seen on the feedback's own page. */
export function PictureMark() {
  return <Camera role="img" aria-label="Has a screenshot" className="size-4 text-muted-foreground" />;
}

export function FeedbackCard({ feedback, now = Date.now() }: { feedback: Feedback; now?: number }) {
  return (
    <article className="relative">
      <Card size="sm" className="relative gap-3 transition-shadow hover:shadow-lg">
        <div className="space-y-3 px-(--card-spacing)">
          <FeedbackMeta feedback={feedback} now={now} />
          <Link
            to={`/feedback/${encodeURIComponent(feedback.id)}`}
            className="block after:absolute after:inset-0 after:content-['']"
          >
            <p className="line-clamp-4 text-sm whitespace-pre-line wrap-anywhere">{feedback.message}</p>
          </Link>
          {feedback.reply && <ReplyNote reply={feedback.reply} clamp />}
          <div className="relative z-10 flex items-center justify-between gap-3">
            {feedback.picture ? <PictureMark /> : <span />}
            <MeToo feedback={feedback} />
          </div>
        </div>
      </Card>
    </article>
  );
}

/**
 * "Me too" with its count, pressed once backed. Posts to the board's action
 * by fetcher, and draws what the tap asked for until the page reads the
 * feedback again. Not offered on your own: its count is a line of text.
 */
export function MeToo({ feedback }: { feedback: Feedback }) {
  const fetcher = useFetcher<{ error?: string }>();
  const asked = fetcher.formData?.get("intent");
  const backed = asked === "back" ? true : asked === "unback" ? false : feedback.backedByMe;
  const count = feedback.backers + Number(backed) - Number(feedback.backedByMe);

  if (feedback.mine) {
    return count > 0 ? <span className="text-sm text-muted-foreground">{count} me too</span> : null;
  }
  return (
    <div className="space-y-1">
      <fetcher.Form method="post" action="/feedback">
        <input type="hidden" name="id" value={feedback.id} />
        <input type="hidden" name="intent" value={backed ? "unback" : "back"} />
        <Button
          type="submit"
          variant={backed ? "default" : "outline"}
          size="sm"
          aria-pressed={backed}
          disabled={fetcher.state !== "idle"}
        >
          Me too{count > 0 && ` · ${count}`}
        </Button>
      </fetcher.Form>
      {fetcher.data?.error && <FieldError>{fetcher.data.error}</FieldError>}
    </div>
  );
}
